import { createContext } from "react";
import { useStore } from "../store";
import { resolveBound } from "../path";
import { BoundValue, Children, Scope } from "../types";

/**
 * Provided by Form with its resolved submitAction name (null outside a form
 * or when the form declares no submitAction). Button uses it to decide
 * whether it is the form's submit affordance (defer to the form's onSubmit —
 * exactly one dispatch per click) or an independent button (type="button",
 * own dispatch, never implicitly submits the enclosing form).
 */
export const FormSubmitContext = createContext<string | null>(null);

/**
 * Reactive read of a BoundValue. Re-renders only when the resolved value
 * actually changes (Zustand's strict-equality selector compare).
 */
export function useBound<T = unknown>(bv: BoundValue | undefined, scope: Scope): T | null {
  return useStore((s) => resolveBound(bv, s.dataModel, scope) as T | null);
}

/**
 * Like useBound but returns a primitive default if the bound value is null/undefined.
 */
export function useBoundOr<T>(bv: BoundValue | undefined, scope: Scope, fallback: T): T {
  const v = useBound<T>(bv, scope);
  return v == null ? fallback : v;
}

/**
 * Read a Children declaration from fields. Returns either a list of child IDs
 * (explicit) or a template descriptor.
 */
export function readChildren(fields: Record<string, unknown>): Children | null {
  const c = fields.children;
  if (!c || typeof c !== "object") return null;
  return c as Children;
}

/**
 * Convenience writer — used by input components on user edits.
 */
export function writePath(path: string, value: unknown): void {
  useStore.getState().writeDataPath(path, value);
}
